import {
    ColumnType,
    Filter,
    getFilterElementsDefault,
    Input,
    Pending,
    PhoneNumber,
    toInt,
    useWorkplaceState
} from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import { downloadReport, getReport, getReportDetailsList } from 'axios/reportApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { map } from 'mappers/mapper';
import { reportDetailsToWorkplace } from 'mappers/reportDetails';
import { ReportDetails as ReportDetailsType } from 'models/api/report';
import DataGridProp from 'models/dataGridProp';
import Id from 'models/id';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { useQuery, useQueryClient } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import { isDataTimeString } from 'utils/dataTime';
import ButtonReportDetailsDownload from './ButtonReportDetailsDownload';
import ReportDetailsFilter from './ReportDetailsFilter';

type DetailsTableDataType = DataGridProp & ReportDetailsType;
type DetailsFilterDataType = Filter<Id & { type: string }>;
type CustomColumnType = ColumnType<DetailsTableDataType, DetailsTableDataType[]>;

const ReportDetails = () => {
    const { t, i18n } = useTranslation();
    const { id: urlId } = useParams();
    const id = toInt(urlId);
    const api = useApi();
    const { hasRole } = useAuth();
    const queryClient = useQueryClient();
    const [columns, setColumns] = useState<CustomColumnType[]>([]);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const currentData = useRef<(Id & ReportDetailsType)[]>();

    const {
        data: selectList,
        isFetching: isReportTypesFetching,
        refetch: refetchReportTypes,
    } = useQuery('reportTypes', () => getSelectList(api, ['ReportTypes'], i18n.language));

    const reportTypes = selectList?.['ReportTypes'] || [];

    const { data: report, isFetching: isReportFetching } = useQuery(['report', id], () =>
        getReport(api, id || 0)
    );

    useEffect(() => {
        refetchReportTypes();
    }, [i18n.language]);

    return id !== undefined && report ? (
        <BOWorkplace<DetailsTableDataType, DetailsTableDataType, DetailsFilterDataType>
            pageTitle="BackOfficeReports"
            filterParams={{}}
            initColumns={(
                (report.type === 'BalanceDetail' && hasRole('reportsGetDetailsExcel')
                    ? [
                          {
                              key: 'actions',
                              name: (
                                  <Link
                                      to="#"
                                      onClick={(e) => {
                                          e.preventDefault();
                                          if (currentData.current) {
                                              if (
                                                  selectedRows.length === currentData.current.length
                                              ) {
                                                  setSelectedRows([]);
                                              } else {
                                                  setSelectedRows(
                                                      currentData.current.map(
                                                          (i) =>
                                                              toInt(
                                                                  i['DetailId'].value as
                                                                      | string
                                                                      | number
                                                              ) || 0
                                                      )
                                                  );
                                              }
                                          }
                                      }}
                                  >
                                      <Trans i18nKey="BackOfficeAll" />
                                  </Link>
                              ),
                              cellClass: 'base-data-grid__cell_centered no-change-modal',
                              headerCellClass: 'base-data-grid__cell_centered',
                              width: 70,
                              minWidth: 70,
                              draggable: false,
                              resizable: false,
                              clickDisable: true,
                              formatter: ({ row }: { row: DetailsTableDataType }) => {
                                  const id = row['DetailId'].value as string | number;
                                  return (
                                      <Input
                                          type="checkbox"
                                          checked={selectedRows.indexOf(toInt(id) || 0) !== -1}
                                          onChange={(e) => {
                                              if (e.target.checked) {
                                                  setSelectedRows([
                                                      ...selectedRows,
                                                      toInt(id) || 0,
                                                  ]);
                                              } else {
                                                  setSelectedRows([
                                                      ...selectedRows.filter((i) => i !== id),
                                                  ]);
                                              }
                                          }}
                                      />
                                  );
                              },
                          },
                      ]
                    : []) as CustomColumnType[]
            ).concat(columns)}
            storagePath={`reportDetails${report.type}/columns`}
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'id',
                    name: t('BackOfficeReportId'),
                },
                {
                    id: 'type',
                    name: t('BackOfficeReportType'),
                    formatter: (val) =>
                        reportTypes.find((t) => t.id?.toString() === val)?.name || '',
                },
            ])}
            loadingFilter={isReportTypesFetching || isReportFetching}
            filter={<ReportDetailsFilter id={id} reportTypes={reportTypes} type={report.type} />}
            fetchQueryFn={async (filter) => {
                setSelectedRows([]);
                const request = map(
                    getReportDetailsList(api, id, filter),
                    reportDetailsToWorkplace
                );

                request.then((resp) => {
                    currentData.current = resp.data;
                    if (resp.data && resp.data[0] && !columns.length) {
                        const firstItem = resp.data[0];
                        const newColumns: CustomColumnType[] = Object.keys(firstItem)
                            .filter((i) => i !== 'id')
                            .map((key) => ({
                                key,
                                name: (
                                    <Trans
                                        i18nKey={'BackOfficeReportHeader' + key}
                                        defaults={key}
                                    />
                                ),
                                formatter: ({ row }: { row: DetailsTableDataType }) => {
                                    const current = row[key as keyof DetailsTableDataType];
                                    if (current) {
                                        if (current.type === 'money') {
                                            return (
                                                <Money value={current.value as number | string} />
                                            );
                                        } else if (isDataTimeString(current.value.toString())) {
                                            return (
                                                <DataTimeText value={current.value.toString()} />
                                            );
                                        } else if (key.toLowerCase() === 'phonenumber') {
                                            return (
                                                <PhoneNumber
                                                    value={current.value as number | string}
                                                />
                                            );
                                        } else if (current.type === 'boolean') {
                                            return current.value === true ? (
                                                <div className="text-success text-center">
                                                    <FaCheckSquare />
                                                </div>
                                            ) : (
                                                <div className="text-danger text-center">
                                                    <FaRegSquare />
                                                </div>
                                            );
                                        }
                                    }

                                    return <>{current.value}</>;
                                },
                            }));

                        setColumns(newColumns);
                    }
                });

                return request;
            }}
            queryKey={['reportDetails', id.toString()]}
            queryClient={queryClient}
            fetchExcelQueryFn={async (filter) => (await downloadReport(api, id, filter)).data}
            buttons={
                report.type === 'BalanceDetail' && hasRole('reportsGetDetailsExcel')
                    ? [<ButtonReportDetailsDownload key="1" items={selectedRows} />]
                    : []
            }
            showExcelBtn={hasRole('reportsExcel')}
        />
    ) : (
        <Pending />
    );
};

const useReportDetailsWorkplaceState = () =>
    useWorkplaceState<DetailsTableDataType, DetailsTableDataType>();

export default ReportDetails;
export { useReportDetailsWorkplaceState };
